import { debugModeAtom } from "@/atoms/debugModeAtom";
import { userAtom } from "@/atoms/userAtom";
import { SwipeDirection } from "@/constants/swipeConstants";
import { InteractionType } from "@/generatedApi/userUserInteractionLoggerApi";
import { useSpotInteraction } from "@/hooks/useSpotInteraction";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import { useTapHandler } from "@/hooks/useTapHandler";
import { useDislikeInteraction } from "@/hooks/useDislikeInteraction";
import { aiLevelPointSuffix, calculateAiLevelPoints } from "@/utils/aiLevelPoints";
import { motion } from "framer-motion";
import { useAtomValue } from "jotai";
import { BookmarkIcon, FlagIcon } from "lucide-react";
import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { SpotCard, type SpotCardProps } from "../SpotCard";
import { DislikeConfirmModal } from "../DislikeConfirmModal";

// SpotCardのpropsからタップハンドラー関連を除外
type SpotCardPropsWithoutHandlers = Omit<
  SpotCardProps,
  "onLeftTap" | "onRightTap" | "isProcessing"
>;

interface SwipeableCardProps extends SpotCardPropsWithoutHandlers {
  // SwipeableCard固有のprops
  recommendationId: string;
  spotName?: string;
  spotDistance?: number;
  cardPosition?: number;
  totalRecommendations?: number;
}

export const SwipeableCard = ({
  // SwipeableCard固有のprops
  recommendationId,
  spotName,
  spotDistance,
  cardPosition,
  totalRecommendations,
  // SpotCardのprops
  spotId,
  ...spotCardProps
}: SwipeableCardProps) => {
  const [swiped, setSwiped] = React.useState(false);
  const [interactionId, setInteractionId] = React.useState<string | null>(null);

  const user = useAtomValue(userAtom);

  const { createInteraction, createDislikeInteraction } = useSpotInteraction({
    spotName,
    spotDistance,
    cardPosition,
    recommendationId,
    totalRecommendations
  });

  const handleCreateInteractionItem = useCallback(
    async (
      interactionType: InteractionType,
      interactionMethod: "swipe" | "button" | "tap" = "swipe"
    ) => {
      try {
        const newInteractionId = await createInteraction(
          spotId,
          interactionType,
          interactionMethod
        );
        setInteractionId(newInteractionId);
      } catch (error) {
        setSwiped(false);
        throw error;
      }
    },
    [createInteraction, spotId]
  );

  const handleSave = useCallback(
    async (method: "swipe" | "button" | "tap" = "swipe") =>
      handleCreateInteractionItem(InteractionType.SAVED, method),
    [handleCreateInteractionItem]
  );

  const handleVisited = useCallback(
    async (method: "swipe" | "button" | "tap" = "swipe") =>
      handleCreateInteractionItem(InteractionType.VISITED, method),
    [handleCreateInteractionItem]
  );

  const onSwiped = useCallback(
    (direction: SwipeDirection) => {
      setSwiped(true);
      const action = direction === "right" ? handleSave("swipe") : handleVisited("swipe");
      const actionType = direction === "right" ? "saved" : "visited";

      toast.promise(action, {
        loading: actionType === "saved" ? "追加中..." : "行ったに追加中...",
        success:
          actionType === "saved"
            ? `行きたいに追加しました！${aiLevelPointSuffix(
                calculateAiLevelPoints(InteractionType.SAVED)
              )}`
            : `行ったに追加しました！${aiLevelPointSuffix(
                calculateAiLevelPoints(InteractionType.VISITED)
              )}`,
        error: "エラーが発生しました"
      });
    },
    [handleSave, handleVisited]
  );

  const { x, rotate, rightOpacity, rightScale, leftOpacity, leftScale, handleDragEnd } =
    useSwipeGesture({
      onSwipe: onSwiped
    });

  const onTapAction = useCallback(
    async (direction: "left" | "right") => {
      const actionType = direction === "right" ? InteractionType.SAVED : InteractionType.VISITED;
      await handleCreateInteractionItem(actionType, "tap");
    },
    [handleCreateInteractionItem]
  );

  const getTapToastMessage = useCallback((direction: "left" | "right") => {
    return direction === "right"
      ? `行きたいに追加しました！${aiLevelPointSuffix(
          calculateAiLevelPoints(InteractionType.SAVED)
        )}`
      : `行ったに追加しました！${aiLevelPointSuffix(
          calculateAiLevelPoints(InteractionType.VISITED)
        )}`;
  }, []);

  const { handleTap, isProcessing } = useTapHandler({
    x,
    swiped,
    setSwiped,
    onAction: onTapAction,
    getToastMessage: getTapToastMessage
  });

  const debugMode = useAtomValue(debugModeAtom);

  // 興味なしインタラクション
  const [isDisliked, setIsDisliked] = useState(false);

  const handleDislikeConfirmAction = useCallback(async () => {
    try {
      await createDislikeInteraction(spotId, "disliked");
      toast.success(
        `興味なしに追加しました！${aiLevelPointSuffix(
          calculateAiLevelPoints(InteractionType.DISLIKED)
        )}`
      );
      setIsDisliked(true);
      setSwiped(true);
    } catch (error) {
      toast.error("エラーが発生しました");
    }
  }, [createDislikeInteraction, spotId]);

  const { showDislikeModal, setShowDislikeModal, handleDislikeClick, handleDislikeConfirm } =
    useDislikeInteraction({ onConfirm: handleDislikeConfirmAction });

  return swiped ? (
    debugMode ? (
      <div>スワイプ済み: {interactionId}</div>
    ) : null
  ) : (
    <div style={{ position: "relative", width: "100%", cursor: "grab" }}>
      {/* 右スワイプ → Save (Bookmark) */}
      <motion.div
        style={{
          position: "absolute",
          top: 30,
          right: -20,
          opacity: rightOpacity,
          scale: rightScale,
          pointerEvents: "none"
        }}
        className="z-50 flex h-16 w-16 flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#0fff78] to-[#86faff] p-2"
      >
        <BookmarkIcon size={24} className="text-white" fill="currentColor" />
      </motion.div>

      {/* 左スワイプ → Visited (Flag) */}
      <motion.div
        style={{
          position: "absolute",
          top: 30,
          left: -20,
          opacity: leftOpacity,
          scale: leftScale,
          pointerEvents: "none"
        }}
        className="z-50 flex h-16 w-16 flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#0fff78] to-[#86faff] p-2"
      >
        <FlagIcon size={24} className="text-white" fill="currentColor" />
      </motion.div>
      <motion.div
        style={{
          x,
          rotate,
          touchAction: "pan-y" // スクロールとの競合防止
        }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={handleDragEnd}
      >
        <SpotCard
          spotId={spotId}
          {...spotCardProps}
          onLeftTap={() => handleTap("left")}
          onRightTap={() => handleTap("right")}
          isProcessing={isProcessing}
          onDislikeClick={handleDislikeClick}
          isDisliked={isDisliked}
          recommendationId={recommendationId}
          trackingUserId={(user as { userId?: string } | null)?.userId}
          trackingCardPosition={cardPosition ?? null}
        />
      </motion.div>

      {/* 興味なし確認モーダル */}
      <DislikeConfirmModal
        isOpen={showDislikeModal}
        onClose={() => setShowDislikeModal(false)}
        onConfirm={handleDislikeConfirm}
      />
    </div>
  );
};
